import React from 'react';
import { supabase } from '../../supabaseClient';
import { 
    Wallet, TrendingUp, AlertTriangle, ShieldCheck, 
    Clock, Activity, Phone, Mail, UserCheck, 
    Save, Edit2, Shield, CheckCircle, XCircle, 
    Landmark, Users, Pin, Plus, Globe, FileText, Target, ChevronRight, Lock, RefreshCw, ShieldAlert, MessageSquare, Key, Server, Copy, ExternalLink
} from 'lucide-react';

// =========================================================
// 1. CUSTOMER JOURNEY WIDGET (Automated)
// =========================================================
export const JourneyWidget = ({ client, liveStages }) => {
    const FLOW_STEPS = [
        { id: 'registered', label: 'Registered', icon: UserCheck },
        { id: 'kyc_pending', label: 'KYC Pending', icon: Clock },
        { id: 'kyc_approved', label: 'KYC Approved', icon: CheckCircle },
        { id: 'first_deposit', label: 'First Deposit', icon: Wallet },
        { id: 'active', label: 'Active', icon: TrendingUp }
    ];

    const calculateCurrentStep = () => {
        // 🚨 THE FIX: Listen directly to the new master journey stage from ClientProfile
        if (client.journeyStage === 'active') return 4;
        
        if (client.journeyStage === 'kyc_approved') {
            // Check if they are funded to push them to the Deposit/Active steps
            const isFunded = Number(client.banking?.balance || 0) > 0 || 
                             Number(client.xvalley?.equity || 0) > 0 || 
                             Number(client.crypto?.balances?.length || 0) > 0;
            return isFunded ? 3 : 2;
        }
        
        if (client.journeyStage === 'kyc_rejected') return 1; // Hold the blue progress line at Pending
        if (client.journeyStage === 'kyc_pending') return 1;
        
        return 0; // registered
    };

    const currentStepIndex = calculateCurrentStep();
    
    // 🚨 THE FIX: Explicitly detect the new 'kyc_rejected' journey stage!
    const isKYCDeclined = client.journeyStage === 'kyc_rejected' || client.kycStatus === 'Rejected' || client.kycStatus === 'Flagged';

    return (
        <div className="col-span-1 md:col-span-2 lg:col-span-3 mb-2 p-6 bg-slate-800/30 rounded-2xl border border-slate-800 overflow-x-auto animate-fade-in">
             <div className="flex justify-between items-end mb-4">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest">Customer Journey</h3>
                {isKYCDeclined && (
                    <span className="px-2 py-1 bg-red-500/10 border border-red-500/50 text-red-500 text-[10px] font-bold uppercase rounded">
                        Action Required: KYC Rejected
                    </span>
                )}
             </div>

             <div className="flex items-center min-w-[700px]">
                {FLOW_STEPS.map((step, index) => {
                    const isCompleted = index <= currentStepIndex;
                    const isCurrent = index === currentStepIndex;
                    const isDeclinedStep = isKYCDeclined && index === 2;
                    
                    let circleColor = "bg-slate-900 border-slate-700 text-slate-600"; 
                    if (isDeclinedStep) {
                        circleColor = "bg-red-500 border-red-400 text-white shadow-[0_0_15px_rgba(239,68,68,0.5)]";
                    } else if (isCurrent) {
                        circleColor = "bg-blue-600 border-blue-400 text-white shadow-[0_0_15px_rgba(37,99,235,0.5)] scale-110";
                    } else if (isCompleted) {
                        circleColor = "bg-slate-800 border-blue-600 text-blue-500";
                    }

                    return (
                        <div key={step.id} className="flex-1 flex items-center relative group">
                            {index !== 0 && (
                                <div className={`absolute left-0 right-[50%] h-1 top-4 -translate-x-1/2 w-full -z-10 ${index <= currentStepIndex ? 'bg-blue-600' : 'bg-slate-700'}`}></div>
                            )}
                            <div className={`flex flex-col items-center gap-2 w-full transition-opacity ${isCompleted || isDeclinedStep ? 'opacity-100' : 'opacity-40'}`}>
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs border-2 transition-all ${circleColor} z-10`}>
                                    {isDeclinedStep ? <XCircle size={16} /> : (isCompleted && !isCurrent ? <CheckCircle size={14} /> : <step.icon size={14} />)}
                                </div>
                                <span className={`text-[10px] font-bold uppercase tracking-wider text-center ${isDeclinedStep ? 'text-red-400' : (isCurrent ? 'text-blue-400' : 'text-slate-500')}`}>
                                    {isDeclinedStep ? 'KYC Declined' : step.label}
                                </span>
                            </div>
                        </div>
                    );
                })}
             </div>
        </div>
    );
};

// ==========================================
// 2. FINANCIAL PULSE (WALLET) - LAZY LOADING
// ==========================================
export const WalletWidget = ({ client, isPro, onForceProvision }) => {
    const [activeAccountIndex, setActiveAccountIndex] = React.useState(0);
    const [activeModal, setActiveModal] = React.useState(null);
    const [tradeView, setTradeView] = React.useState('open');

    const [ledgerData, setLedgerData] = React.useState({ openTrades: [], closedTrades: [], transactions: [] });
    const [isLoadingLedger, setIsLoadingLedger] = React.useState(false);

    // 1. Variables defined first
    const accounts = client.tradingAccounts && client.tradingAccounts.length > 0 ? client.tradingAccounts : [];
    const activeAccount = accounts[activeAccountIndex] || {};
    const accountId = activeAccount.id || 'N/A';

    // 2. React Hook MUST be called before any "return" statements
    React.useEffect(() => {
        if (!accountId || accountId === 'N/A') return;

        let isMounted = true;
        const fetchAccountLedger = async () => {
            setIsLoadingLedger(true);
            try {
                const [tradesRes, historyRes, txRes] = await Promise.all([
    fetch(`${process.env.REACT_APP_XVALLEY_API_URL}/api/xvalley/trades/${accountId}`).catch(()=>null),
    fetch(`${process.env.REACT_APP_XVALLEY_API_URL}/api/xvalley/history/${accountId}`).catch(()=>null),
    fetch(`${process.env.REACT_APP_XVALLEY_API_URL}/api/xvalley/transactions/${accountId}`).catch(()=>null)
]);

                const trades = tradesRes?.ok ? (await tradesRes.json()).trades || [] : [];
                const history = historyRes?.ok ? (await historyRes.json()).history || [] : [];
                const txs = txRes?.ok ? (await txRes.json()).transactions || [] : [];

                if (isMounted) {
                    setLedgerData({ openTrades: trades, closedTrades: history, transactions: txs });
                }
            } catch (err) {
                console.error("Lazy fetch failed:", err);
            } finally {
                if (isMounted) setIsLoadingLedger(false);
            }
        };

        fetchAccountLedger();
        return () => { isMounted = false; };
    }, [accountId]);

    // 3. Early return comes AFTER the Hook
    if (accounts.length === 0) {
        return (
            <div className="bg-slate-800/50 p-6 rounded-2xl border border-slate-700 flex flex-col justify-center items-center text-center h-full min-h-[250px]">
                 <Wallet size={32} className="text-slate-600 mb-3" />
                 <p className="text-slate-400 font-bold">No Trading Accounts Found</p>
                 <p className="text-xs text-slate-500 mt-1">Client has not opened an account yet.</p>
                 
                 <button 
                     onClick={onForceProvision}
                     className="mt-6 px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-lg transition-colors flex items-center justify-center gap-2 shadow-lg"
                 >
                     <Server size={14} /> Provision Trading Account
                 </button>
                 
            </div>
        );
    }

    const activeTradesList = tradeView === 'open' ? ledgerData.openTrades : ledgerData.closedTrades;

    const liveEquity = Number(activeAccount.equity || 0);
    const liveDeposit = Number(client.deposit || 0);
    const liveWithdrawals = Number(client.withdrawals || 0);
    const netDeposit = liveDeposit - liveWithdrawals;
    
    const liveBalance = Number(activeAccount.balance || 0);
    const liveCredit = Number(activeAccount.credit || 0); 
    const totalFunds = liveBalance + liveCredit;
    const bonusRatio = totalFunds > 0 ? (liveCredit / totalFunds) : 0; 
    const accountName = activeAccount.type || 'Nommia Trader';

    const handleNext = () => setActiveAccountIndex((prev) => (prev + 1) % accounts.length);
    const handlePrev = () => setActiveAccountIndex((prev) => (prev === 0 ? accounts.length - 1 : prev - 1));

    return (
        <div className="bg-slate-800/50 p-6 rounded-2xl border border-slate-700 relative overflow-hidden h-full min-h-[250px] flex flex-col justify-between">
            
            <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                    <div className="p-3 bg-blue-500/10 rounded-xl text-blue-400"><Wallet size={24} /></div>
                    <div>
                        <div className="flex items-center gap-2">
                            {accounts.length > 1 && <button onClick={handlePrev} className="bg-slate-700 hover:bg-slate-600 text-white p-1 rounded transition-colors shadow"><ChevronRight size={14} className="rotate-180" /></button>}
                            
                            <h4 className="text-sm font-bold text-white truncate max-w-[140px]" title={accountName}>{accountName}</h4>
                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${activeAccount.isLive ? 'bg-green-500/20 text-green-400 border border-green-500/30 shadow-[0_0_10px_rgba(34,197,94,0.2)]' : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'}`}>
                                {activeAccount.isLive ? 'LIVE' : 'DEMO'}
                            </span>
                            
                            {accounts.length > 1 && <button onClick={handleNext} className="bg-slate-700 hover:bg-slate-600 text-white p-1 rounded transition-colors shadow"><ChevronRight size={14} /></button>}
                        </div>
                        <p className="text-[10px] text-slate-500 uppercase font-bold tracking-wider mt-1">
                            XValley • <span className="text-blue-400">ACC: {accountId}</span>
                            {accounts.length > 1 && <span className="ml-2 text-slate-600">({activeAccountIndex + 1}/{accounts.length})</span>}
                        </p>
                    </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                    {client.xvalley && <span className="text-[10px] font-bold bg-green-500/10 text-green-400 px-2 py-1 rounded border border-green-500/20 animate-pulse whitespace-nowrap">LIVE WAMP</span>}
                </div>
            </div>
            
            <div className="mb-6 flex justify-between items-end">
                <div>
                    <p className="text-slate-400 text-xs font-bold uppercase tracking-wider">Net Equity</p>
                    <h3 className="text-3xl font-bold text-white mt-1">
                        {client.banking?.currency === 'GBP' ? '£' : '€'}{liveEquity.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </h3>
                    <p className={`text-xs font-mono mt-1 ${netDeposit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        Global Net Deposit: {netDeposit >= 0 ? '+' : ''}{netDeposit.toLocaleString()}
                    </p>
                    {Number(client.pamm_deposit) > 0 && (
                        <p className="text-xs font-mono text-purple-400 mt-1 flex items-center gap-1.5" title="Allocated in PAMM pool">
                            <span className="w-1.5 h-1.5 rounded-full bg-purple-400"></span>
                            PAMM Allocation: ${Number(client.pamm_deposit).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            {Number(client.pamm_live_equity) > 0 && (
                                <span className="text-emerald-400 ml-1">
                                    (Live Eq: ${Number(client.pamm_live_equity).toLocaleString(undefined, { minimumFractionDigits: 2 })})
                                </span>
                            )}
                        </p>
                    )}
                </div>
                <button onClick={() => alert(`Open Deposit Modal for account: ${accountId}`)} className="bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold py-2.5 px-4 rounded-lg shadow-lg flex items-center gap-2">
                    <Plus size={14} /> Deposit
                </button>
            </div>

            <div>
                <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden flex mb-2">
                    <div className="h-full bg-blue-500" style={{ width: `${(1 - bonusRatio) * 100}%` }}></div>
                    <div className="h-full bg-purple-500" style={{ width: `${bonusRatio * 100}%` }}></div>
                </div>
                <div className="flex justify-between text-[10px] font-bold uppercase text-slate-500">
                    <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-blue-500"></div> Cash ({liveBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })})</span>
                    {bonusRatio > 0 && <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-purple-500"></div> Credit ({liveCredit.toLocaleString()})</span>}
                    {activeAccount.marginUsed > 0 && <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-amber-500"></div> Margin ({activeAccount.marginUsed.toLocaleString(undefined, { minimumFractionDigits: 2 })})</span>}
                </div>
            </div>

            <div className="flex gap-2 pt-4 mt-4 border-t border-slate-700/50">
                <button onClick={() => { setActiveModal('trades'); setTradeView('open'); }} className="flex-1 bg-slate-900 hover:bg-slate-700 text-white text-[11px] font-bold py-2 rounded border border-slate-700 transition-colors flex justify-center items-center gap-2">
                    {isLoadingLedger ? <RefreshCw size={12} className="animate-spin text-slate-500"/> : `Trades (${ledgerData.openTrades.length})`}
                </button>
                <button onClick={() => setActiveModal('transactions')} className="flex-1 bg-slate-900 hover:bg-slate-700 text-white text-[11px] font-bold py-2 rounded border border-slate-700 transition-colors flex justify-center items-center gap-2">
                    {isLoadingLedger ? <RefreshCw size={12} className="animate-spin text-slate-500"/> : `Ledger (${ledgerData.transactions.length})`}
                </button>
            </div>

            {/* TRADING TOP-UP WALLETS */}
            <div className="pt-4 mt-4 border-t border-slate-700/50">
                <div className="mb-2">
                    <label className="text-xs text-slate-500 block font-bold text-slate-400">Trading Top-Up Wallets</label>
                    <p className="text-[9px] text-amber-500/80 mt-0.5 uppercase tracking-wider">Funds {'>'} $45 are swept nightly to Nommia Treasury</p>
                </div>
                
                {(client.topupAddresses?.evm || client.topupAddresses?.btc || client.topupAddresses?.tron) ? (
                    <div className="space-y-2">
                        {/* EVM Address */}
                        {client.topupAddresses.evm && (
                            <div className="flex items-center justify-between bg-slate-900/40 p-2.5 rounded-lg border border-slate-700/50">
                                <div className="flex-1 overflow-hidden pr-2">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded bg-purple-500/20 text-purple-400 border border-purple-500/30">
                                            EVM
                                        </span>
                                        <span className="text-[9px] text-slate-500">Polygon, Base, Arbitrum, BSC</span>
                                    </div>
                                    <p className="text-[10px] text-slate-300 font-mono break-all leading-tight">
                                        {client.topupAddresses.evm}
                                    </p>
                                </div>
                                <div className="flex gap-1">
                                    <button 
                                        onClick={() => navigator.clipboard.writeText(client.topupAddresses.evm)}
                                        className="p-1.5 text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 rounded transition-colors"
                                        title="Copy EVM Address"
                                    >
                                        <Copy size={14} />
                                    </button>
                                    <a 
                                        href={`https://debank.com/profile/${client.topupAddresses.evm}`}
                                        target="_blank" rel="noreferrer"
                                        className="p-1.5 text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 rounded transition-colors flex items-center justify-center"
                                        title="View on Explorer"
                                    >
                                        <ExternalLink size={14} />
                                    </a>
                                </div>
                            </div>
                        )}

                        {/* BTC Address */}
                        {client.topupAddresses.btc && (
                            <div className="flex items-center justify-between bg-slate-900/40 p-2.5 rounded-lg border border-slate-700/50">
                                <div className="flex-1 overflow-hidden pr-2">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded bg-orange-500/20 text-orange-400 border border-orange-500/30">
                                            BTC
                                        </span>
                                        <span className="text-[9px] text-slate-500">Bitcoin Network</span>
                                    </div>
                                    <p className="text-[10px] text-slate-300 font-mono break-all leading-tight">
                                        {client.topupAddresses.btc}
                                    </p>
                                </div>
                                <div className="flex gap-1">
                                    <button 
                                        onClick={() => navigator.clipboard.writeText(client.topupAddresses.btc)}
                                        className="p-1.5 text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 rounded transition-colors"
                                        title="Copy BTC Address"
                                    >
                                        <Copy size={14} />
                                    </button>
                                    <a 
                                        href={`https://www.blockchain.com/explorer/addresses/btc/${client.topupAddresses.btc}`}
                                        target="_blank" rel="noreferrer"
                                        className="p-1.5 text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 rounded transition-colors flex items-center justify-center"
                                        title="View on Explorer"
                                    >
                                        <ExternalLink size={14} />
                                    </a>
                                </div>
                            </div>
                        )}

                        {/* TRON Address */}
                        {client.topupAddresses.tron && (
                            <div className="flex items-center justify-between bg-slate-900/40 p-2.5 rounded-lg border border-slate-700/50">
                                <div className="flex-1 overflow-hidden pr-2">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded bg-red-500/20 text-red-400 border border-red-500/30">
                                            TRON
                                        </span>
                                        <span className="text-[9px] text-slate-500">TRC-20</span>
                                    </div>
                                    <p className="text-[10px] text-slate-300 font-mono break-all leading-tight">
                                        {client.topupAddresses.tron}
                                    </p>
                                </div>
                                <div className="flex gap-1">
                                    <button 
                                        onClick={() => navigator.clipboard.writeText(client.topupAddresses.tron)}
                                        className="p-1.5 text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 rounded transition-colors"
                                        title="Copy TRON Address"
                                    >
                                        <Copy size={14} />
                                    </button>
                                    <a 
                                        href={`https://tronscan.org/#/address/${client.topupAddresses.tron}`}
                                        target="_blank" rel="noreferrer"
                                        className="p-1.5 text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 rounded transition-colors flex items-center justify-center"
                                        title="View on Explorer"
                                    >
                                        <ExternalLink size={14} />
                                    </a>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="bg-slate-900/40 p-3 rounded-lg border border-slate-700/50 text-center">
                        <p className="text-xs text-slate-500">No Top-Up Wallets Assigned</p>
                    </div>
                )}
            </div>

            {/* MODALS */}
            {activeModal && (
                <div className="fixed inset-0 z-[80] bg-slate-950/80 flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in">
                    <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col max-h-[80vh]">
                        
                        {/* Header */}
                        <div className="p-4 border-b border-slate-800 bg-slate-950/50 flex justify-between items-center">
                            <h3 className="font-bold text-white flex items-center gap-2">
                                {activeModal === 'trades' ? <TrendingUp size={16} className="text-blue-500" /> : <Landmark size={16} className="text-purple-500" />} 
                                {activeModal === 'trades' ? `Trading History - ACC: ${accountId}` : `Transaction Ledger - ACC: ${accountId}`}
                            </h3>
                            <button onClick={() => setActiveModal(null)} className="text-slate-400 hover:text-white transition-colors"><XCircle size={20} /></button>
                        </div>

                        {/* Trade View Toggles */}
                        {activeModal === 'trades' && (
                            <div className="flex gap-4 px-6 pt-4 bg-slate-900">
                                <button onClick={() => setTradeView('open')} className={`pb-2 text-sm font-bold border-b-2 transition-all ${tradeView === 'open' ? 'border-blue-500 text-blue-400' : 'border-transparent text-slate-500 hover:text-slate-400'}`}>
                                    Open Trades ({ledgerData.openTrades.length})
                                </button>
                                <button onClick={() => setTradeView('closed')} className={`pb-2 text-sm font-bold border-b-2 transition-all ${tradeView === 'closed' ? 'border-blue-500 text-blue-400' : 'border-transparent text-slate-500 hover:text-slate-400'}`}>
                                    Closed / History ({ledgerData.closedTrades.length})
                                </button>
                            </div>
                        )}
                        
                        <div className="p-4 overflow-y-auto custom-scrollbar flex-1 bg-slate-900">
                            {isLoadingLedger ? (
                                <div className="text-center py-10 text-slate-500"><RefreshCw size={24} className="animate-spin mx-auto mb-2 opacity-50"/> Fetching XValley Data...</div>
                            ) : activeModal === 'trades' && activeTradesList.length > 0 ? (
                                <div className="space-y-2">
                                    {activeTradesList.map((trade, i) => {
                                        const direction = trade.SNa || (trade.Cmd === 0 ? 'Buy' : 'Sell');
                                        const isBuy = direction.toLowerCase() === 'buy';
                                        const symbol = trade.Instrument?.Name || trade.Symbol || 'Asset';
                                        const volume = trade.VU || trade.Volume || 0;
                                        const profit = trade.TotalProfit || trade.PL || trade.Profit || 0;

                                        return (
                                        <div key={i} className="bg-slate-800/50 p-3 rounded-lg border border-slate-700 flex justify-between items-center text-sm hover:bg-slate-800 transition-colors">
                                            <div>
                                                <span className={`font-bold text-[10px] px-2 py-0.5 rounded uppercase mr-3 ${isBuy ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
                                                    {direction}
                                                </span>
                                                <span className="text-white font-bold">{symbol}</span>
                                                <span className="text-slate-500 ml-3 text-xs">Vol: {volume}</span>
                                            </div>
                                            <div className={`font-bold font-mono ${profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                                {profit > 0 ? '+' : ''}{Number(profit).toFixed(2)}
                                            </div>
                                        </div>
                                    )})}
                                </div>
                            ) : activeModal === 'transactions' && ledgerData.transactions.length > 0 ? (
                                <div className="space-y-2">
                                    {ledgerData.transactions.map((tx, i) => {
                                            const type = tx.TSN || tx.Comment || 'Transaction';
                                            const provider = tx.T?.Name || tx.Provider || 'System';
                                            const amount = tx.TA || tx.Amount || 0;
                                            const time = tx.D ? tx.D.replace('T', ' ').substring(0, 16) : (tx.CloseTime ? tx.CloseTime.replace('T', ' ') : 'N/A');
                                            
                                            // 🚨 BULLETPROOF MAPPER: Checks multiple keys and converts to a lowercase string to catch everything
                                            const rawState = tx.StS !== undefined ? tx.StS : (tx.St !== undefined ? tx.St : (tx.State !== undefined ? tx.State : (tx.Status !== undefined ? tx.Status : 2)));
                                            const stateStr = String(rawState).toLowerCase();

                                            let stateLabel = 'Unknown';
                                            let stateColor = 'text-slate-500 bg-slate-800';
                                            let amountStyle = amount >= 0 ? 'text-green-400' : 'text-red-400';

                                            // Map numbers AND literal strings to their CRM equivalents
                                            if (stateStr === '1' || stateStr === 'pending') { stateLabel = 'Pending'; stateColor = 'text-amber-400 bg-amber-500/10 border-amber-500/20'; }
                                            else if (stateStr === '2' || stateStr === 'completed') { stateLabel = 'Completed'; stateColor = 'text-green-400 bg-green-500/10 border-green-500/20'; }
                                            else if (stateStr === '3' || stateStr === 'cancelled') { stateLabel = 'Cancelled'; stateColor = 'text-slate-400 bg-slate-800 border-slate-700'; amountStyle = 'text-slate-500 line-through'; }
                                            else if (stateStr === '4' || stateStr === 'failed') { stateLabel = 'Failed'; stateColor = 'text-red-400 bg-red-500/10 border-red-500/20'; amountStyle = 'text-slate-500 line-through'; }
                                            else if (stateStr === '5' || stateStr === 'deleted' || stateStr === 'invalid') { stateLabel = 'Deleted'; stateColor = 'text-red-400 bg-red-500/10 border-red-500/20'; amountStyle = 'text-slate-500 line-through'; }
                                            else {
                                                // If we somehow still miss it, log it to the browser console so we can see the exact raw data!
                                                console.log("⚠️ Unmapped TX State:", rawState, "Full TX Data:", tx);
                                                stateLabel = 'Completed'; // Fallback
                                                stateColor = 'text-green-400 bg-green-500/10 border-green-500/20';
                                            }

                                            return (
                                            <div key={i} className="bg-slate-800/50 p-3 rounded-lg border border-slate-700 flex justify-between items-center text-sm hover:bg-slate-800 transition-colors">
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-white font-bold block">{type}</span>
                                                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border uppercase tracking-wider ${stateColor}`}>
                                                            {stateLabel}
                                                        </span>
                                                    </div>
                                                    <span className="text-[10px] text-slate-500 font-mono mt-1 block">
                                                        {time} • Provider: {provider}
                                                    </span>
                                                </div>
                                                <div className={`font-bold font-mono ${amountStyle}`}>
                                                    {amount > 0 && stateLabel !== 'Cancelled' && stateLabel !== 'Failed' && stateLabel !== 'Deleted' ? '+' : ''}{Number(amount).toFixed(2)}
                                                </div>
                                            </div>
                                        )})}
                                </div>
                            ) : (
                                <div className="text-center py-10 border border-dashed border-slate-700 rounded-xl bg-slate-950/30 text-slate-500 text-sm italic">
                                    No data found.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// =========================================================
// 3. BANKING WIDGET (Now with Transaction History Modal)
// =========================================================
export const BankingWidget = ({ client }) => {
    const [txModal, setTxModal] = React.useState({ isOpen: false, title: '', transactions: [] });

    return (
        <div className="bg-slate-800/50 p-6 rounded-2xl border border-slate-700 relative flex flex-col h-full">
            <div className="absolute top-0 right-0 p-2 bg-amber-500/10 border-l border-b border-amber-500/20 text-amber-400 text-[10px] font-bold rounded-bl-xl backdrop-blur">BANK</div>
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2">
                <Landmark size={14} /> Banking
            </h3>
            <div className="space-y-6 flex-1 flex flex-col min-h-0">
                
                {/* FIAT ACCOUNT */}
                <div>
                    <div className="flex justify-between items-center mb-1">
                        <label className="text-xs text-slate-500 block">FIAT Account</label>
                        <button 
                            onClick={() => setTxModal({ isOpen: true, title: 'FIAT Transactions', transactions: client.banking?.transactions || [] })}
                            className="text-[10px] bg-slate-800 hover:bg-slate-700 text-slate-300 px-2 py-1 rounded transition-colors border border-slate-700"
                        >
                            View History
                        </button>
                    </div>
                    <p className="text-2xl font-bold text-white">
                        {client.banking?.currency === 'GBP' ? '£' : '€'}
                        {Number(client.banking?.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                    <p className="text-xs text-slate-500 font-mono mt-1">
                        {client.banking?.iban || 'No IBAN Linked'}
                    </p>
                </div>
                
                {/* CRYPTO WALLET */}
                <div className="pt-4 border-t border-slate-700 flex-1 flex flex-col min-h-0">
                    <div className="flex justify-between items-center mb-2">
                        <label className="text-xs text-slate-500 block">Crypto Wallets</label>
                        <button 
                            onClick={() => setTxModal({ isOpen: true, title: 'Crypto Transactions', transactions: client.crypto?.transactions || [] })}
                            className="text-[10px] bg-slate-800 hover:bg-slate-700 text-slate-300 px-2 py-1 rounded transition-colors border border-slate-700"
                        >
                            View History
                        </button>
                    </div>
                    
                    {/* Scrollable list for multiple crypto assets */}
                    <div className="space-y-2 overflow-y-auto custom-scrollbar pr-1 flex-1">
                        {client.crypto?.balances?.length > 0 ? (
                            client.crypto.balances.map((coin, index) => (
                                <div key={index} className="flex items-center justify-between bg-slate-900/40 p-2.5 rounded-lg border border-slate-700/50">
                                    <div className="flex-1">
                                        <p className="text-lg font-bold text-white leading-none flex items-center">
                                            {Number(coin.amount || coin.balance || coin.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })}
                                            <span className="text-[10px] font-normal text-slate-400 ml-1.5 uppercase tracking-wider">
                                                {coin.name === 'Bitcoin' ? 'BTC' : coin.name === 'Ethereum' ? 'ETH' : coin.name === 'Tether' ? 'USDT' : coin.name || 'Crypto'}
                                            </span>
                                        </p>
                                        <p className="text-[10px] text-slate-500 font-mono mt-1.5 break-all pr-2">
                                            {client.crypto?.address || coin.walletAddress || 'No Wallet Linked'}
                                        </p>
                                    </div>
                                    {coin.network && (
                                        <div className="ml-2">
                                            <span className={`px-2 py-1 text-[9px] font-bold uppercase tracking-wider rounded-md ${
                                                coin.network === 'Base' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' :
                                                coin.network === 'Polygon' ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' :
                                                coin.network === 'Arbitrum' ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' :
                                                'bg-slate-700 text-slate-300'
                                            }`}>
                                                {coin.network}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            ))
                        ) : (
                            <div>
                                <p className="text-2xl font-bold text-white">0.00</p>
                                <p className="text-xs text-slate-500 font-mono mt-1">No Active Wallets</p>
                            </div>
                        )}
                    </div>
                </div>
             </div>

            {/* TRANSACTIONS MODAL OVERLAY */}
            {txModal.isOpen && (
                <div className="fixed inset-0 z-[80] bg-slate-950/80 flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in">
                    <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
                        <div className="p-4 border-b border-slate-800 bg-slate-950/50 flex justify-between items-center">
                            <h3 className="font-bold text-white flex items-center gap-2">
                                <Activity size={16} className="text-blue-500" /> {txModal.title}
                            </h3>
                            <button onClick={() => setTxModal({ isOpen: false, title: '', transactions: [] })} className="text-slate-400 hover:text-white transition-colors">
                                <XCircle size={20} />
                            </button>
                        </div>
                        <div className="p-4 max-h-[50vh] overflow-y-auto custom-scrollbar">
                            {txModal.transactions.length > 0 ? (
                                <div className="space-y-3">
                                    {txModal.transactions.map((tx, i) => (
                                        <div key={i} className="bg-slate-800/50 p-3 rounded-xl border border-slate-700 flex justify-between items-center hover:bg-slate-800 transition-colors">
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <p className="text-sm font-bold text-white capitalize">
                                                        {tx.type === 'sepaDeposit' ? 'SEPA Deposit' : tx.type === 'cryptoDeposit' ? 'Crypto Deposit' : tx.type === 'cryptoWithdrawal' ? 'Crypto Withdrawal' : tx.type}
                                                    </p>
                                                    {tx.network && (
                                                        <span className={`px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider rounded ${
                                                            tx.network === 'Base' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' :
                                                            tx.network === 'Polygon' ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' :
                                                            tx.network === 'Arbitrum' ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' :
                                                            'bg-slate-700 text-slate-300'
                                                        }`}>
                                                            {tx.network}
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-[10px] text-slate-400 font-mono mt-0.5">
                                                    {new Date(tx.date).toLocaleDateString()} • {new Date(tx.date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <p className={`text-sm font-bold ${tx.type?.toLowerCase().includes('withdrawal') ? 'text-white' : 'text-green-400'}`}>
                                                    {tx.type?.toLowerCase().includes('withdrawal') ? '-' : '+'}{tx.amount} {tx.currency}
                                                </p>
                                                <p className={`text-[10px] font-bold uppercase mt-0.5 tracking-wider ${tx.status === 'completed' ? 'text-green-500' : 'text-amber-500'}`}>
                                                    {tx.status}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="py-10 text-center border border-dashed border-slate-700 rounded-xl bg-slate-950/30">
                                    <p className="text-slate-500 text-sm italic">No recent transactions found.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// =========================================================
// 4. RISK WIDGET (Production - Live IP & Fraud Monitoring)
// =========================================================
export const RiskWidget = ({ client }) => {
    // --- SMART AML SCORE GENERATOR ---
    const generateAMLScore = () => {
        const apiScore = Number(client.amlScore);
        if (apiScore > 0 && apiScore !== 12) return apiScore;

        let riskScore = 5;
        const riskLevel = (client.risk || client.riskLevel || '').toLowerCase();
        if (riskLevel === 'high') riskScore += 40;
        if (riskLevel === 'medium') riskScore += 20;
        if (client.pep || client.pepStatus) riskScore += 35;
        if (client.fatca) riskScore += 15;
        
        const kyc = (client.kycStatus || client.kyc?.status || '').toUpperCase();
        if (kyc !== 'APPROVED' && kyc !== 'VERIFIED') riskScore += 25;

        return riskScore;
    };

    let baseScore = generateAMLScore();
    
    // --- 🚨 LIVE IP & FRAUD DETECTION ---
    const expectedCountry = client.country && client.country !== 'Unknown' ? client.country : 'Cyprus';
    
    // 🚨 THE FIX: Removed the mock data. Strictly reads from Supabase now.
    const recentLogs = client.securityEvents || [];
    const lastLogin = recentLogs.length > 0 ? recentLogs[0] : null;
    
    // THE ENGINE: Only flag if a real login exists, and it explicitly mismatches the KYC country
    const isIpAnomaly = lastLogin && lastLogin.location && lastLogin.location !== 'Unknown' && !lastLogin.location.includes(expectedCountry);

    const displayScore = isIpAnomaly ? Math.min(baseScore + 50, 99) : Math.min(baseScore, 99);
    
    const scoreColor = displayScore < 30 ? 'text-green-400' : displayScore < 60 ? 'text-amber-400' : 'text-red-400';
    const currentRiskLabel = isIpAnomaly ? 'CRITICAL' : (client.risk || client.riskLevel || 'LOW');

    return (
        <div className={`p-6 rounded-2xl border transition-all duration-500 ${isIpAnomaly ? 'bg-red-950/20 border-red-500/50 shadow-[0_0_20px_rgba(239,68,68,0.15)]' : 'bg-slate-800/50 border-slate-700'}`}>
            <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-3">
                    {isIpAnomaly ? <AlertTriangle size={20} className="text-red-500 animate-pulse" /> : <ShieldCheck size={20} className="text-amber-500" />}
                    <h3 className="font-bold text-white">Risk Profile</h3>
                </div>
                <span className={`text-xs font-bold px-2 py-1 rounded border uppercase tracking-wider ${
                    currentRiskLabel === 'CRITICAL' ? 'text-red-100 bg-red-600 border-red-500 animate-pulse shadow-[0_0_10px_rgba(239,68,68,0.5)]' :
                    currentRiskLabel.toLowerCase() === 'high' ? 'text-red-400 bg-red-500/10 border-red-500/20' : 
                    currentRiskLabel.toLowerCase() === 'medium' ? 'text-amber-400 bg-amber-500/10 border-amber-500/20' : 
                    'text-green-400 bg-green-500/10 border-green-500/20'
                }`}>
                    {currentRiskLabel} RISK
                </span>
            </div>
            
            <div className="space-y-4">
                <div className="flex justify-between text-sm">
                    <span className="text-slate-400">KYC Status</span>
                    <span className={`font-bold ${
                        client.kycStatus === 'Approved' || client.kycStatus === 'VERIFIED' ? 'text-green-400' : 
                        client.kycStatus === 'Pending' ? 'text-amber-400' : 'text-red-400'
                    }`}>
                        {client.kycStatus || client.kyc?.status || 'Pending'}
                    </span>
                </div>
                
                <div className="flex justify-between text-sm items-center">
                    <span className="text-slate-400">Live AML Score</span>
                    <span className={`font-bold ${scoreColor}`}>
                        {displayScore}/100
                    </span>
                </div>
                
                <div className={`p-3 rounded-lg border flex flex-col gap-2 mt-2 ${isIpAnomaly ? 'bg-red-500/10 border-red-500/30' : 'bg-slate-900 border-slate-800'}`}>
                    <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                            <Activity size={14} className={isIpAnomaly ? "text-red-400" : "text-slate-500"} />
                            <span className={`text-[10px] font-bold uppercase tracking-wider ${isIpAnomaly ? 'text-red-400' : 'text-slate-400'}`}>
                                {isIpAnomaly ? 'SECURITY ALERT: IP MISMATCH' : 'Last Login Location'}
                            </span>
                        </div>
                    </div>
                    
                    <div className="flex justify-between items-center bg-slate-950/50 p-2 rounded border border-slate-800/50">
                        <div>
                            <p className="text-[9px] text-slate-500 uppercase">Registered (KYC)</p>
                            <p className="text-xs text-white font-mono">{expectedCountry}</p>
                        </div>
                        {isIpAnomaly ? (
                            <ChevronRight size={14} className="text-red-500" />
                        ) : (
                            <div className="h-px w-4 bg-slate-700"></div>
                        )}
                        <div className={isIpAnomaly ? "text-right" : ""}>
                            <p className="text-[9px] text-slate-500 uppercase">Actual (Live IP)</p>
                            <p className={`text-xs font-mono ${isIpAnomaly ? 'text-red-400 font-bold' : lastLogin ? 'text-white' : 'text-slate-500 italic'}`}>
                                {lastLogin ? lastLogin.location : 'Awaiting Login...'}
                            </p>
                        </div>
                    </div>
                    
                    {isIpAnomaly && (
                        <button 
                            onClick={() => alert(`API CALL INITIATED: Trading account temporarily locked for ${client.email}. Client forced to re-verify via Bio-Liveness.`)}
                            className="mt-2 w-full bg-red-600 hover:bg-red-500 text-white text-[11px] font-bold py-2.5 rounded transition-colors flex items-center justify-center gap-2 shadow-lg"
                        >
                            <Lock size={14} /> Lock Trading Account
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

// =========================================================
// 5. ASSIGNED AGENT
// =========================================================
export const AgentPerformanceWidget = ({ client }) => {
    // 🚨 THE FIX: Removed client.referrer so it strictly defaults to the Unassigned Queue
    const agentName = client?.assignedAgent || 'Unassigned';
    const agentInitials = agentName !== 'Unassigned' 
        ? agentName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() 
        : 'U';

    // Smart extraction: Look for the most recent outbound communication
    const recentComms = client?.communications || [];
    const lastComm = recentComms.find(c => c.status === 'Sent' || c.messages?.[0]?.sender !== 'Client');
    
    // Safely format the contact time and activity description
    const lastContact = lastComm 
        ? `${lastComm.date} • ${lastComm.messages?.[0]?.time || ''}` 
        : (client?.lastContactDate || 'No contact recorded');
        
    const lastActivity = lastComm 
        ? `Sent ${lastComm.type.charAt(0).toUpperCase() + lastComm.type.slice(1)}: "${lastComm.subject}"` 
        : (client?.lastAgentActivity || 'Account Assigned');

    return (
        <div className="bg-slate-900 p-6 rounded-2xl border border-slate-800 h-full flex flex-col">
            {/* Top Section: Agent Identity */}
            <div className="flex justify-between items-center pb-6 border-b border-slate-800/50 mb-4">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-300 font-bold text-lg shadow-inner">
                        {agentInitials}
                    </div>
                    <div>
                        <p className="text-[10px] text-slate-500 uppercase font-bold tracking-wider mb-0.5">Assigned Agent</p>
                        <h3 className="font-bold text-white text-lg leading-none">{agentName}</h3>
                    </div>
                </div>
                <button 
                    onClick={() => alert('Opening Reassignment Protocol...')}
                    className="text-[10px] font-bold bg-slate-800 text-slate-300 px-2 py-1 rounded hover:bg-slate-700 transition-colors"
                >
                    Reassign
                </button>
            </div>

            {/* Bottom Section: Relationship Status */}
            <div className="flex flex-col gap-4 flex-1 justify-center">
                <div>
                    <p className="text-[10px] text-slate-500 uppercase font-bold tracking-wider mb-1.5 flex items-center gap-1.5">
                        <Activity size={12} className="text-blue-400" /> Last Activity
                    </p>
                    <div className="bg-slate-800/50 p-3 rounded-lg border border-slate-700/50">
                        <p className="text-sm font-bold text-slate-200 truncate" title={lastActivity}>
                            {lastActivity}
                        </p>
                    </div>
                </div>
                
                <div>
                    <p className="text-[10px] text-slate-500 uppercase font-bold tracking-wider mb-1.5 flex items-center gap-1.5">
                        <Clock size={12} className="text-amber-400" /> Last Contact
                    </p>
                    <div className="bg-slate-800/50 p-3 rounded-lg border border-slate-700/50">
                        <p className="text-sm font-bold text-white font-mono">
                            {lastContact}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

// =========================================================
// 6. CLIENT DETAILS WIDGET (Fully Wired to Supabase)
// =========================================================
export const ClientDetailsWidget = ({ client, onUpdateClient }) => {
    const [isEditing, setIsEditing] = React.useState(false);
    const [isSaving, setIsSaving] = React.useState(false);
    
    // We use local state to track what the agent is typing before they hit save
    const [editData, setEditData] = React.useState({});

    // Sync local state when the client data loads
    React.useEffect(() => {
        // Safe DOB handling
        const rawDob = client.dob || client.dateOfBirth || '';
        const safeDob = rawDob === 'N/A' ? '' : rawDob;

        setEditData({
            firstName: client.firstName || '',
            lastName: client.lastName || '',
            phone: client.phone || '',
            country: client.country || '',
            role: client.role || 'Trader',
            dob: safeDob, 
            nationality: client.nationality || '',
            pep: client.pep || false,
            fatca: client.fatca || false,
            // Safe Address handling
            street: client.address?.street || '',
            city: client.address?.city || '',
            postCode: client.address?.postCode || client.address?.postalCode || ''
        });
    }, [client, isEditing]);

    const handleSave = async () => {
        if (!onUpdateClient) return setIsEditing(false); // Safety fallback
        
        setIsSaving(true);
        const result = await onUpdateClient(editData);
        
        if (result.success) {
            setIsEditing(false);
        } else {
            alert("Failed to save changes: " + result.message);
        }
        setIsSaving(false);
    };

    return (
        <div className="col-span-1 md:col-span-2 lg:col-span-3 space-y-6 animate-fade-in">
             <div className="flex justify-between items-center">
                <h3 className="text-lg font-bold text-white">Detailed Information</h3>
                <button 
                    onClick={() => isEditing ? handleSave() : setIsEditing(true)} 
                    disabled={isSaving}
                    className={`px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-colors ${
                        isEditing ? 'bg-green-600 hover:bg-green-500 text-white' : 'bg-slate-800 text-slate-300 border border-slate-700 hover:bg-slate-700'
                    } disabled:opacity-50`}
                >
                    {isSaving ? <RefreshCw size={16} className="animate-spin" /> : isEditing ? <Save size={16} /> : <Edit2 size={16} />} 
                    {isSaving ? 'Saving...' : isEditing ? 'Save Changes' : 'Edit Details'}
                </button>
            </div>
            
            {/* SUMMARY */}
            <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
                <div className="p-4 border-b border-slate-800 bg-slate-950/50">
                    <h4 className="text-sm font-bold text-slate-400 uppercase">Summary</h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 p-6">
                    <div>
                        <label className="text-xs text-slate-500 block mb-1">First Name</label>
                        {isEditing ? <input className="bg-slate-800 border border-slate-700 rounded p-2 text-sm text-white w-full outline-none focus:border-blue-500" value={editData.firstName} onChange={e => setEditData({...editData, firstName: e.target.value})} /> : <p className="text-white font-medium">{client.firstName}</p>}
                    </div>
                    <div>
                        <label className="text-xs text-slate-500 block mb-1">Last Name</label>
                        {isEditing ? <input className="bg-slate-800 border border-slate-700 rounded p-2 text-sm text-white w-full outline-none focus:border-blue-500" value={editData.lastName} onChange={e => setEditData({...editData, lastName: e.target.value})} /> : <p className="text-white font-medium">{client.lastName}</p>}
                    </div>
                    <div>
                        <label className="text-xs text-slate-500 block mb-1">Email</label>
                        {isEditing ? (
                            <input className="bg-slate-800 border border-slate-700 rounded p-2 text-sm text-slate-500 w-full cursor-not-allowed" disabled value={client.email} title="Email cannot be changed here" />
                        ) : (
                            <div className="flex items-center gap-2">
                                <p className="text-white font-medium">{client.email}</p>
                                {client.emailVerified ? <CheckCircle size={14} className="text-green-500" title="Email Verified"/> : <XCircle size={14} className="text-slate-500" title="Unverified"/>}
                            </div>
                        )}
                    </div>
                    <div>
                        <label className="text-xs text-slate-500 block mb-1">Phone</label>
                        {isEditing ? (
                            <input className="bg-slate-800 border border-slate-700 rounded p-2 text-sm text-white w-full outline-none focus:border-blue-500" value={editData.phone} onChange={e => setEditData({...editData, phone: e.target.value})} />
                        ) : (
                            <div className="flex items-center gap-2">
                                <p className="text-white font-medium">{client.phone}</p>
                                {client.phoneVerified ? <CheckCircle size={14} className="text-green-500" title="Phone Verified"/> : <XCircle size={14} className="text-slate-500" title="Unverified"/>}
                            </div>
                        )}
                    </div>
                    <div>
                        <label className="text-xs text-slate-500 block mb-1">Role</label>
                        {isEditing ? (
                            <select className="bg-slate-800 border border-slate-700 rounded p-2 text-sm text-white w-full outline-none focus:border-blue-500" value={editData.role} onChange={e => setEditData({...editData, role: e.target.value})}>
                                <option>Trader</option>
                                <option>IB</option>
                            </select>
                        ) : (
                            <span className="bg-blue-900/30 text-blue-400 px-2 py-1 rounded text-xs font-bold uppercase">{client.role || 'Trader'}</span>
                        )}
                    </div>
                    <div>
                        <label className="text-xs text-slate-500 block mb-1">Country</label>
                        {isEditing ? <input className="bg-slate-800 border border-slate-700 rounded p-2 text-sm text-white w-full outline-none focus:border-blue-500" value={editData.country} onChange={e => setEditData({...editData, country: e.target.value})} /> : <p className="text-white font-medium">{client.country || 'Unknown'}</p>}
                    </div>
                    <div>
                        <label className="text-xs text-slate-500 block mb-1">Referrer (Invited By)</label>
                        {isEditing ? <input disabled className="bg-slate-800 border border-slate-700 rounded p-2 text-sm text-slate-500 w-full cursor-not-allowed" value={client.referrer} /> : <p className="text-amber-500 font-mono text-sm">{client.referrer || 'Direct Sign-up'}</p>}
                    </div>
                    <div>
                        <label className="text-xs text-slate-500 block mb-1">Referral Code (Own)</label>
                        <p className="font-mono font-medium text-slate-200 bg-slate-900 px-2 py-1 rounded w-fit border border-slate-700">{client.referralCode || 'N/A'}</p>
                    </div>

                    <div>
                        <label className="text-xs text-slate-500 block mb-1">Date Registered</label>
                        {isEditing ? (
                            <input disabled className="bg-slate-800 border border-slate-700 rounded p-2 text-sm text-slate-500 w-full cursor-not-allowed" value={client.registeredDate || 'Unknown'} title="Registration date is permanently logged by the system" />
                        ) : (
                            <p className="text-white font-medium">{client.registeredDate || 'Unknown'}</p>
                        )}
                    </div>
                    
                </div>
            </div>
            
            {/* PII */}
            <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
                <div className="p-4 border-b border-slate-800 bg-slate-950/50">
                    <h4 className="text-sm font-bold text-slate-400 uppercase">Personal Details (PII)</h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 p-6">
                    <div>
                        <label className="text-xs text-slate-500 block mb-1">DOB</label>
                        {isEditing ? <input type="date" className="bg-slate-800 border border-slate-700 rounded p-2 text-sm text-white w-full outline-none focus:border-blue-500" value={editData.dob} onChange={e => setEditData({...editData, dob: e.target.value})} /> : <p className="text-white font-medium">{client.dob}</p>}
                    </div>
                    <div>
                        <label className="text-xs text-slate-500 block mb-1">Nationality</label>
                        {isEditing ? <input className="bg-slate-800 border border-slate-700 rounded p-2 text-sm text-white w-full outline-none focus:border-blue-500" value={editData.nationality} onChange={e => setEditData({...editData, nationality: e.target.value})} /> : <p className="text-white font-medium">{client.nationality || '-'}</p>}
                    </div>
                    <div>
                        <label className="text-xs text-slate-500 block mb-1">PEP Status</label>
                        {isEditing ? (
                            <select className="bg-slate-800 border border-slate-700 rounded p-2 text-sm text-white w-full outline-none focus:border-blue-500" value={editData.pep} onChange={e => setEditData({...editData, pep: e.target.value === 'true'})}>
                                <option value="false">False</option>
                                <option value="true">True</option>
                            </select>
                        ) : (
                            <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${client.pep ? 'bg-red-500 text-white' : 'bg-slate-800 text-slate-400'}`}>
                                {client.pep ? 'YES (High Risk)' : 'NO'}
                            </span>
                        )}
                    </div>
                    <div>
                        <label className="text-xs text-slate-500 block mb-1">FATCA Status</label>
                        {isEditing ? (
                            <select className="bg-slate-800 border border-slate-700 rounded p-2 text-sm text-white w-full outline-none focus:border-blue-500" value={editData.fatca} onChange={e => setEditData({...editData, fatca: e.target.value === 'true'})}>
                                <option value="false">False</option>
                                <option value="true">True</option>
                            </select>
                        ) : (
                            <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${client.fatca ? 'bg-red-500 text-white' : 'bg-slate-800 text-slate-400'}`}>
                                {client.fatca ? 'YES (US Person)' : 'NO'}
                            </span>
                        )}
                    </div>
                    
                    {/* ADDRESS ROW - Spans full width */}
                    <div className="col-span-1 md:col-span-2 lg:col-span-4 pt-4 border-t border-slate-800/50 mt-2">
                        <label className="text-xs text-slate-500 block mb-2">Full Address</label>
                        {isEditing ? (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <input className="bg-slate-800 border border-slate-700 rounded p-2 text-sm text-white w-full outline-none focus:border-blue-500" placeholder="Street" value={editData.street} onChange={e => setEditData({...editData, street: e.target.value})} />
                                <input className="bg-slate-800 border border-slate-700 rounded p-2 text-sm text-white w-full outline-none focus:border-blue-500" placeholder="City" value={editData.city} onChange={e => setEditData({...editData, city: e.target.value})} />
                                <input className="bg-slate-800 border border-slate-700 rounded p-2 text-sm text-white w-full outline-none focus:border-blue-500" placeholder="Post Code" value={editData.postCode} onChange={e => setEditData({...editData, postCode: e.target.value})} />
                            </div>
                        ) : (
                            <p className="text-white font-medium">
                                {client.address 
                                    ? [client.address.number, client.address.street, client.address.city, client.address.postCode, client.address.country].filter(Boolean).join(', ')
                                    : 'No address on file'}
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// =========================================================
// 7. TRADING PULSE (Live P&L, Margin & XValley Ledgers)
// =========================================================
export const TradingPulseWidget = ({ client }) => {
    const [activeModal, setActiveModal] = React.useState(null);
    const [ledgerData, setLedgerData] = React.useState({ openTrades: [], transactions: [] });
    const [isLoading, setIsLoading] = React.useState(false);

    // Safely grab the XValley account ID (assuming first account if multiple)
    const accountId = client.tradingAccounts?.[0]?.id || client.xvalley?.id || null;

    // Prioritize Live XValley Data
    const liveEquity = Number(client.xvalley?.equity || 0);
    const liveBalance = Number(client.xvalley?.balance || 0);
    const rawPnL = client.xvalley ? (liveEquity - liveBalance) : 0.00;
    const openPnL = Number(rawPnL.toFixed(2));
    const marginLevel = Number(client.xvalley?.marginLevel || 0); 
    const isProfit = openPnL >= 0;

    // 🚨 THE FIX: Fetch data ON DEMAND only when a modal is opened
    React.useEffect(() => {
        if (!activeModal || !accountId) return;

        let isMounted = true;
        const fetchLedgerData = async () => {
            setIsLoading(true);
            try {
                if (activeModal === 'trades') {
                    const XVALLEY_BASE = process.env.REACT_APP_XVALLEY_API_URL;
                    const res = await fetch(`${XVALLEY_BASE}/api/xvalley/trades/${accountId}`);
                    const data = res.ok ? await res.json() : { trades: [] };
                    if (isMounted) setLedgerData(prev => ({ ...prev, openTrades: data.trades || [] }));
                } 
                else if (activeModal === 'transactions') {
                    const res = await fetch(`${process.env.REACT_APP_XVALLEY_API_URL}/api/xvalley/transactions/${accountId}`);
                    const data = res.ok ? await res.json() : { transactions: [] };
                    if (isMounted) setLedgerData(prev => ({ ...prev, transactions: data.transactions || [] }));
                }
            } catch (err) {
                console.error("XValley Data Fetch Error:", err);
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        fetchLedgerData();
        return () => { isMounted = false; };
    }, [activeModal, accountId]);

    return (
        <div className="bg-slate-800/50 p-6 rounded-2xl border border-slate-700 flex flex-col justify-between group hover:border-blue-500/30 transition-all">
            <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2 text-slate-400">
                    <Activity size={16} className={isProfit ? "text-green-500" : "text-red-500"} />
                    <span className="text-xs font-bold uppercase tracking-wider">Open P&L</span>
                </div>
                {client.xvalley ? (
                    <span className="text-[10px] bg-green-500/10 text-green-400 border border-green-500/20 px-2 py-1 rounded font-mono flex items-center gap-1 animate-pulse">
                        <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div> LIVE
                    </span>
                ) : (
                    <span className="text-[10px] bg-slate-900 text-slate-500 px-2 py-1 rounded font-mono">OFFLINE</span>
                )}
            </div>
            
            <div className="mb-4">
                <h3 className={`text-3xl font-bold transition-colors ${isProfit ? 'text-green-400' : 'text-red-400'}`}>
                    {openPnL > 0 ? '+' : ''}{openPnL.toLocaleString(undefined, { minimumFractionDigits: 2 })} USD
                </h3>
            </div>
            
            <div className="space-y-1 mb-4">
                <div className="flex justify-between text-[10px] font-bold uppercase text-slate-500">
                    <span>Margin Level</span>
                    <span className={marginLevel < 100 ? "text-red-500" : "text-blue-400"}>{marginLevel.toLocaleString(undefined, { maximumFractionDigits: 1 })}%</span>
                </div>
                <div className="w-full h-1.5 bg-slate-700 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all duration-1000 ${marginLevel < 100 ? 'bg-red-500' : 'bg-blue-500'}`} style={{ width: `${Math.min(marginLevel, 100)}%` }}></div>
                </div>
                {marginLevel < 100 && marginLevel > 0 && <p className="text-[10px] text-red-400 flex items-center gap-1 mt-1"><AlertTriangle size={10} /> Margin Call Warning</p>}
            </div>
            
            <div className="grid grid-cols-2 gap-2 pt-4 border-t border-slate-700/50 mb-4">
                 <div><span className="text-[10px] text-slate-500 block uppercase">Open Lots</span><span className="text-sm font-bold text-white">{client.xvalley?.openLots || '0.00'}</span></div>
                 <div className="text-right"><span className="text-[10px] text-slate-500 block uppercase">Margin Used</span><span className="text-sm font-bold text-slate-300">${Number(client.xvalley?.margin || 0).toLocaleString()}</span></div>
            </div>

            {/* ACTION BUTTONS */}
            <div className="flex gap-2 pt-2 border-t border-slate-700/50 mt-auto">
                <button 
                    onClick={() => setActiveModal('trades')}
                    disabled={!accountId}
                    className="flex-1 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-[11px] font-bold py-2 rounded border border-slate-700 transition-colors"
                >
                    View Trades
                </button>
                <button 
                    onClick={() => setActiveModal('transactions')}
                    disabled={!accountId}
                    className="flex-1 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-[11px] font-bold py-2 rounded border border-slate-700 transition-colors"
                >
                    Ledger
                </button>
            </div>

            {/* MODALS */}
            {activeModal && (
                <div className="fixed inset-0 z-[80] bg-slate-950/80 flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in">
                    <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col max-h-[80vh]">
                        <div className="p-4 border-b border-slate-800 bg-slate-950/50 flex justify-between items-center">
                            <h3 className="font-bold text-white flex items-center gap-2">
                                {activeModal === 'trades' ? <TrendingUp size={16} className="text-blue-500" /> : <Landmark size={16} className="text-purple-500" />} 
                                {activeModal === 'trades' ? `Live Open Trades (Acc: ${accountId})` : `XValley Ledger (Acc: ${accountId})`}
                            </h3>
                            <button onClick={() => setActiveModal(null)} className="text-slate-400 hover:text-white transition-colors">
                                <XCircle size={20} />
                            </button>
                        </div>
                        
                        <div className="p-4 overflow-y-auto custom-scrollbar flex-1 bg-slate-900">
                            {isLoading ? (
                                <div className="text-center py-10 text-slate-500">
                                    <RefreshCw size={24} className="animate-spin mx-auto mb-2 opacity-50"/> 
                                    Pulling secure data from XValley...
                                </div>
                            ) : activeModal === 'trades' ? (
                                ledgerData.openTrades.length > 0 ? (
                                    <div className="space-y-2">
                                        {ledgerData.openTrades.map((trade, i) => {
                                            const direction = trade.SNa || (trade.Cmd === 0 ? 'Buy' : 'Sell');
                                            const isBuy = direction.toLowerCase() === 'buy';
                                            const symbol = trade.Instrument?.Name || trade.Symbol || 'Asset';
                                            const volume = trade.VU || trade.Volume || 0;
                                            const profit = trade.TotalProfit || trade.PL || trade.Profit || 0;

                                            return (
                                            <div key={i} className="bg-slate-800/50 p-3 rounded-lg border border-slate-700 flex justify-between items-center text-sm hover:bg-slate-800 transition-colors">
                                                <div>
                                                    <span className={`font-bold text-[10px] px-2 py-0.5 rounded uppercase mr-3 ${isBuy ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
                                                        {direction}
                                                    </span>
                                                    <span className="text-white font-bold">{symbol}</span>
                                                    <span className="text-slate-500 ml-3 text-xs">Vol: {volume}</span>
                                                </div>
                                                <div className={`font-bold font-mono ${profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                                    {profit > 0 ? '+' : ''}{Number(profit).toFixed(2)}
                                                </div>
                                            </div>
                                        )})}
                                    </div>
                                ) : (
                                    <div className="text-center py-10 border border-dashed border-slate-700 rounded-xl bg-slate-950/30">
                                        <p className="text-slate-500 text-sm italic">No open trades found for this account.</p>
                                    </div>
                                )
                            ) : (
                                ledgerData.transactions.length > 0 ? (
                                    <div className="space-y-2">
                                        {ledgerData.transactions.map((tx, i) => {
                                            const type = tx.TSN || tx.Comment || 'Transaction';
                                            const provider = tx.T?.Name || tx.Provider || 'System';
                                            const amount = tx.TA || tx.Amount || 0;
                                            const time = tx.D ? tx.D.replace('T', ' ').substring(0, 16) : (tx.CloseTime ? tx.CloseTime.replace('T', ' ') : 'N/A');
                                            
                                            // 🚨 APPENIX C: Mapping XValley Transaction States
                                            const stateCode = tx.StS !== undefined ? tx.StS : 2; 
                                            let stateLabel = 'Unknown';
                                            let stateColor = 'text-slate-500 bg-slate-800';
                                            let amountStyle = amount >= 0 ? 'text-green-400' : 'text-red-400';

                                            if (stateCode === 1) { stateLabel = 'Pending'; stateColor = 'text-amber-400 bg-amber-500/10 border-amber-500/20'; }
                                            else if (stateCode === 2) { stateLabel = 'Completed'; stateColor = 'text-green-400 bg-green-500/10 border-green-500/20'; }
                                            else if (stateCode === 3) { stateLabel = 'Cancelled'; stateColor = 'text-slate-400 bg-slate-800 border-slate-700'; amountStyle = 'text-slate-500 line-through'; }
                                            else if (stateCode === 4) { stateLabel = 'Failed'; stateColor = 'text-red-400 bg-red-500/10 border-red-500/20'; amountStyle = 'text-slate-500 line-through'; }
                                            else if (stateCode === 5) { stateLabel = 'Deleted'; stateColor = 'text-red-400 bg-red-500/10 border-red-500/20'; amountStyle = 'text-slate-500 line-through'; }

                                            return (
                                            <div key={i} className="bg-slate-800/50 p-3 rounded-lg border border-slate-700 flex justify-between items-center text-sm hover:bg-slate-800 transition-colors">
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-white font-bold block">{type}</span>
                                                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border uppercase tracking-wider ${stateColor}`}>
                                                            {stateLabel}
                                                        </span>
                                                    </div>
                                                    <span className="text-[10px] text-slate-500 font-mono mt-1 block">
                                                        {time} • Provider: {provider}
                                                    </span>
                                                </div>
                                                <div className={`font-bold font-mono ${amountStyle}`}>
                                                    {amount > 0 && stateCode !== 3 && stateCode !== 4 && stateCode !== 5 ? '+' : ''}{Number(amount).toFixed(2)}
                                                </div>
                                            </div>
                                        )})}
                                    </div>
                                ) : (
                                    <div className="text-center py-10 border border-dashed border-slate-700 rounded-xl bg-slate-950/30">
                                        <p className="text-slate-500 text-sm italic">No internal ledger history found on XValley.</p>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// =========================================================
// 8. ACTIVITY FEED (The Timeline)
// =========================================================
export const ActivityFeedWidget = ({ client }) => {
    // 🚨 THE FIX: Added '?.' so it safely handles a missing/loading client object
    const activities = client?.systemNotifications || [];

    // Helper to pick the right icon and color based on the action type
    const getIconConfig = (type) => {
        switch (type?.toLowerCase()) {
            case 'edit': return { icon: Edit2, color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20' };
            case 'security': return { icon: ShieldAlert, color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20' };
            case 'comms': 
            case 'email': return { icon: Mail, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20' };
            case 'file': return { icon: FileText, color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/20' };
            case 'ticket': return { icon: MessageSquare, color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/20' };
            case 'key': return { icon: Key, color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' };
            default: return { icon: Server, color: 'text-slate-400', bg: 'bg-slate-500/10', border: 'border-slate-500/20' };
        }
    };

    return (
        <div className="bg-slate-800/50 p-6 rounded-2xl border border-slate-700 h-full max-h-[400px] flex flex-col">
            <h3 className="font-bold text-white flex items-center gap-2 mb-6 sticky top-0 bg-slate-800/0 backdrop-blur-sm py-2 z-10">
                <Activity size={18} className="text-blue-500" /> Recent Activity
            </h3>
            
            <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
                {activities.length > 0 ? (
                    <div className="relative border-l border-slate-700/50 ml-3 space-y-6 pb-4">
                        {activities.slice(0, 10).map((log, i) => {
                            const { icon: Icon, color, bg, border } = getIconConfig(log.type);
                            return (
                                <div key={log.id || i} className="relative pl-6 animate-fade-in">
                                    <div className={`absolute -left-3.5 top-0.5 w-7 h-7 rounded-full ${bg} ${border} border flex items-center justify-center`}>
                                        <Icon size={12} className={color} />
                                    </div>
                                    <div>
                                        <p className="text-sm font-bold text-slate-200">{log.action}</p>
                                        <p className="text-xs text-slate-400 mt-0.5 leading-snug">{log.desc}</p>
                                        <div className="flex items-center gap-2 mt-1.5">
                                            <span className="text-[10px] text-slate-500 font-mono">{log.time}</span>
                                            <span className="w-1 h-1 rounded-full bg-slate-700"></span>
                                            <span className="text-[10px] text-slate-500 uppercase tracking-wider">{log.actor}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="h-full flex flex-col items-center justify-center text-slate-500 space-y-3 opacity-50 py-10">
                        <Server size={32} />
                        <p className="text-sm font-bold">No recent activity</p>
                    </div>
                )}
            </div>
        </div>
    );
};

// =========================================================
// 9. QUICK NOTES & PINS (Dynamic & Wired to Supabase)
// =========================================================
export const QuickNotesWidget = ({ client, loggedInAgent = "System Agent" }) => {
    const [isEditing, setIsEditing] = React.useState(false);
    const [noteText, setNoteText] = React.useState(client.importantNote || '');
    const [isSaving, setIsSaving] = React.useState(false);
    const [lastUpdatedBy, setLastUpdatedBy] = React.useState(client.noteAuthor || 'System');
    const [lastUpdatedOn, setLastUpdatedOn] = React.useState(client.noteDate || 'Never');

    React.useEffect(() => {
        const fetchNote = async () => {
            try {
                const { data, error } = await supabase
                    .from('client_notes')
                    .select('*')
                    .eq('client_email', client.email)
                    .order('created_at', { ascending: false })
                    .limit(1)
                    .maybeSingle();
                
                if (data && !error) {
                    setNoteText(data.note_content || '');
                    // Table doesn't have an agent_name column, so we default to 'System'
                    setLastUpdatedBy('System');
                    setLastUpdatedOn(new Date(data.created_at).toLocaleString());
                }
            } catch (err) {}
        };
        if (client?.email) fetchNote();
    }, [client?.email]);

    const handleSaveNote = async () => {
        setIsSaving(true);
        try {
            // 🚨 THE FIX: Match the exact columns that exist in the Supabase table!
            const { error } = await supabase
                .from('client_notes')
                .insert([{
                    client_email: client.email,
                    note_content: noteText,
                    staff_email: loggedInAgent.replace(/\s+/g, '.').toLowerCase() + '@nommia.io'
                }]);

            if (!error) {
                setLastUpdatedBy(loggedInAgent);
                setLastUpdatedOn('Just now');
                setIsEditing(false);
            } else {
                console.error("Supabase Save Error:", error);
                alert("Failed to save note. Please ensure Row Level Security (RLS) is disabled or allows inserts on the client_notes table.");
            }
        } catch (error) {
            console.error("Error saving note:", error);
        } finally {
            setIsSaving(false);
        }
    };

    // Helper to get agent initials (e.g., "Sarah Jenkins" -> "SJ")
    const getInitials = (name) => {
        return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    };

    return (
        <div className="bg-amber-500/5 p-6 rounded-2xl border border-amber-500/20 h-full flex flex-col transition-all">
            <div className="flex justify-between items-start mb-4">
                <h3 className="font-bold text-amber-500 flex items-center gap-2">
                    <Pin size={18} className="fill-amber-500" /> Important Note
                </h3>
                {!isEditing && (
                    <button 
                        onClick={() => setIsEditing(true)} 
                        className="text-[10px] text-amber-500/70 hover:text-amber-500 underline uppercase tracking-wider"
                    >
                        Edit
                    </button>
                )}
            </div>
            
            <div className="flex-1 flex flex-col">
                {isEditing ? (
                    <div className="flex-1 flex flex-col gap-2 animate-fade-in">
                        <textarea 
                            value={noteText}
                            onChange={(e) => setNoteText(e.target.value)}
                            className="flex-1 w-full bg-slate-900/50 border border-amber-500/30 rounded-lg p-3 text-sm text-amber-100 placeholder-slate-600 focus:outline-none focus:border-amber-500 custom-scrollbar resize-none"
                            placeholder="Enter important client details here..."
                        />
                        <div className="flex justify-end gap-2 mt-2">
                            <button 
                                onClick={() => setIsEditing(false)}
                                className="px-3 py-1.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold transition-colors"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={handleSaveNote}
                                disabled={isSaving}
                                className="px-3 py-1.5 rounded bg-amber-600 hover:bg-amber-500 text-white text-xs font-bold transition-colors flex items-center gap-2 disabled:opacity-50"
                            >
                                {isSaving ? <RefreshCw size={12} className="animate-spin" /> : <Save size={12} />}
                                {isSaving ? 'Saving...' : 'Save Note'}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="flex-1 overflow-y-auto custom-scrollbar pr-2">
                        {noteText ? (
                            <p className="text-sm text-slate-300 italic leading-relaxed whitespace-pre-wrap">
                                "{noteText}"
                            </p>
                        ) : (
                            <p className="text-sm text-slate-600 italic">No notes pinned for this client.</p>
                        )}
                    </div>
                )}
            </div>

            <div className="mt-4 pt-4 border-t border-amber-500/10 flex justify-between items-center">
                <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-slate-700 border border-slate-600 flex items-center justify-center text-[10px] text-amber-400 font-bold">
                        {getInitials(lastUpdatedBy)}
                    </div>
                    <span className="text-[10px] text-slate-500">
                        {lastUpdatedBy} • {lastUpdatedOn}
                    </span>
                </div>
            </div>
        </div>
    );
};

// =========================================================
// 10. MARKETING PULSE
// =========================================================
export const MarketingPulseWidget = () => {
    return (
        <div className="bg-slate-800/50 p-6 rounded-2xl border border-slate-700 flex flex-col justify-between">
            <h3 className="font-bold text-white flex items-center gap-2 mb-4">
                <Target size={18} className="text-purple-400" /> Marketing Intel
            </h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="p-3 bg-slate-900 rounded-lg text-center">
                    <span className="text-[10px] text-slate-500 uppercase block">Email Open Rate</span>
                    <span className="text-lg font-bold text-white">45%</span>
                </div>
                <div className="p-3 bg-slate-900 rounded-lg text-center">
                    <span className="text-[10px] text-slate-500 uppercase block">Click Rate</span>
                    <span className="text-lg font-bold text-blue-400">12%</span>
                </div>
            </div>
            <div className="space-y-2">
                <p className="text-[10px] text-slate-500 uppercase font-bold">Last Campaign Interaction</p>
                <div className="flex items-center gap-3 p-2 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                    <Mail size={16} className="text-purple-400" />
                    <div>
                        <p className="text-xs text-white font-bold">"Gold Volatility Alert"</p>
                        <p className="text-[10px] text-purple-300">Opened 2 hours ago</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

// =========================================================
// FACTORY: THE MAPPER (Now includes ALL widgets)
// =========================================================
// 👇 Added onUpdateClient
export const renderWidget = (widgetId, client, userRole, loggedInAgent, liveStages, onForceProvision, onUpdateClient) => {
    switch (widgetId) {
        case 'wallet_lite': return <WalletWidget key={widgetId} client={client} isPro={false} onForceProvision={onForceProvision} />;
        case 'wallet_pro': return <WalletWidget key={widgetId} client={client} isPro={true} onForceProvision={onForceProvision} />;
        
        case 'risk_gauge': return <RiskWidget key={widgetId} client={client} />;
        case 'agent_perf': return <AgentPerformanceWidget key={widgetId} client={client} viewerRole={userRole} />;
        
        case 'details_pane': return <ClientDetailsWidget key={widgetId} client={client} onUpdateClient={onUpdateClient} />;
        
        case 'journey': return <JourneyWidget key={widgetId} client={client} liveStages={liveStages} />;
        case 'banking_card': return <BankingWidget key={widgetId} client={client} />;
        case 'trading_pulse': return <TradingPulseWidget key={widgetId} client={client} />;
        
        // NEW WIDGETS
        case 'activity_feed': return <ActivityFeedWidget key={widgetId} client={client} />;
        case 'quick_notes': return <QuickNotesWidget key={widgetId} client={client} loggedInAgent={loggedInAgent} />;
        case 'marketing_pulse': return <MarketingPulseWidget key={widgetId} />;
        
        // Buttons
        case 'quick_actions': 
            return <div key={widgetId} className="bg-slate-800/50 p-6 rounded-2xl border border-slate-700 min-h-[150px] flex flex-col justify-center gap-2">
                <button className="bg-blue-600 text-white py-2 rounded text-xs font-bold flex items-center justify-center gap-2"><Phone size={14}/> Call</button>
                <button className="bg-slate-700 text-white py-2 rounded text-xs font-bold flex items-center justify-center gap-2"><Mail size={14}/> Email</button>
            </div>;
        
        default: return null;
    }
};